// Discover one by one (D-081): one post at a time, like a dating app. Left (or ✕) ignores it,
// right (or ♥) saves it to ask later. Drag the card, use the buttons, or the arrow keys; Z undoes.
import type { Post, PostList } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type PointerEvent, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, engagement, post, timeAgo } from "../api";
import { LicensedSong } from "./LicensedSong";
import { PlayVideo } from "./PlayVideo";
import { SavedVideo } from "./SavedVideo";
import { useWorkspace } from "./Shell";
import { useToast } from "./Toast";

type Choice = "no" | "yes";
const THRESHOLD = 110;

function Card({ p, behind = false }: { p: Post; behind?: boolean }) {
  const workspace = useWorkspace();
  const kind = p.mediaType === "video" ? "Video" : p.mediaType === "carousel" ? "Carousel" : null;
  return (
    <div className="swipe-pic-wrap" aria-hidden={behind}>
      <div className="swipe-pic">
        {p.previewUrl && <img src={p.previewUrl} alt="" draggable={false} />}
        {kind && <span className="dkind">{kind}</span>}
        {p.score !== null && <span className="swipe-score">{p.score}</span>}
        {!behind && <PlayVideo post={p} />}
      </div>
      {!behind && (
        <div className="swipe-info stack" style={{ gap: 6 }}>
          <div className="row wrap" style={{ gap: 6 }}>
            <b>{p.creator ? `@${p.creator}` : "Unknown creator"}</b>
            {p.topPick && <span className="pill ok">Top pick</span>}
            <LicensedSong post={p} />
          </div>
          <span className="small muted">
            {p.hashtag ? `via #${p.hashtag} · ` : ""}posted {timeAgo(p.postedAt)}
            {workspace.showEngagement && engagement(p) ? ` · ${engagement(p)}` : ""}
            {p.permalink && (
              <>
                {" · "}
                <a href={p.permalink} target="_blank" rel="noreferrer">
                  Instagram ↗
                </a>
              </>
            )}
          </span>
          <SavedVideo post={p} />
          {p.scoreReason && <span className="small">{p.scoreReason}</span>}
          {p.caption && <p className="small muted swipe-cap">{p.caption}</p>}
        </div>
      )}
    </div>
  );
}

export function SwipeDeck({ sort, onShowSaved }: { sort: string; onShowSaved: () => void }) {
  const { ws } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const deckQuery = useQuery({
    queryKey: ["posts", ws, "discover", "swipe", sort],
    queryFn: () =>
      api<PostList>(`/v1/w/${ws}/posts?tab=discover&sort=${sort}&saved=false&limit=40`),
  });
  // Posts decided here leave the deck at once; the server's list catches up after.
  const [gone, setGone] = useState<string[]>([]);
  const [history, setHistory] = useState<{ id: string; choice: Choice }[]>([]);
  const [savedHere, setSavedHere] = useState(0);
  const [dx, setDx] = useState(0);
  const [leaving, setLeaving] = useState<Choice | null>(null);
  const start = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const deck = (deckQuery.data?.posts ?? []).filter((p) => !gone.includes(p.id));

  const top = deck[0];
  const next = deck[1];
  // The server's count already leaves out posts decided before its last answer.
  const loaded = deckQuery.data?.posts ?? [];
  const left = Math.max(
    0,
    (deckQuery.data?.found?.unsorted ?? 0) -
      gone.filter((id) => loaded.some((p) => p.id === id)).length,
  );

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["posts", ws] });
  }, [qc, ws]);

  const decide = useCallback(
    (choice: Choice) => {
      if (!top || leaving) return;
      const id = top.id;
      setLeaving(choice);
      setTimeout(() => {
        setGone((g) => [...g, id]);
        setHistory((h) => [...h, { id, choice }]);
        if (choice === "yes") setSavedHere((n) => n + 1);
        setLeaving(null);
        setDx(0);
      }, 200);
      const call =
        choice === "no"
          ? post(`/v1/w/${ws}/posts/skip`, { ids: [id], reason: "Not interested" })
          : post(`/v1/w/${ws}/posts/save`, { ids: [id], saved: true });
      call
        .then(() => {
          if (deck.length <= 5) refresh();
        })
        .catch((e) => {
          toast(e instanceof ApiError ? e.message : "That didn't save. Try again.");
          setGone((g) => g.filter((x) => x !== id));
        });
    },
    [top, leaving, ws, deck.length, refresh, toast],
  );

  const undo = useCallback(() => {
    const last = history[history.length - 1];
    if (!last || leaving) return;
    setHistory((h) => h.slice(0, -1));
    const call =
      last.choice === "no"
        ? post(`/v1/w/${ws}/posts/restore`, { ids: [last.id] })
        : post(`/v1/w/${ws}/posts/save`, { ids: [last.id], saved: false });
    call
      .then(() => {
        if (last.choice === "yes") setSavedHere((n) => Math.max(0, n - 1));
        setGone((g) => g.filter((x) => x !== last.id));
        refresh();
      })
      .catch((e) => toast(e instanceof ApiError ? e.message : "That couldn't be undone."));
  }, [history, leaving, ws, refresh, toast]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A video playing in its pop-up keeps the arrow keys.
      if (document.querySelector("dialog[open]")) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT"))
        return;
      if (e.key === "ArrowLeft") decide("no");
      else if (e.key === "ArrowRight") decide("yes");
      else if (e.key === "z" || e.key === "Z") undo();
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [decide, undo]);

  // Load the next picture before it's needed.
  useEffect(() => {
    if (deck[2]?.previewUrl) new Image().src = deck[2].previewUrl;
  }, [deck]);

  const down = (e: PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("a, button")) return;
    start.current = e.clientX;
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    if (start.current !== null) setDx(e.clientX - start.current);
  };
  const up = () => {
    if (start.current === null) return;
    start.current = null;
    setDragging(false);
    if (dx > THRESHOLD) decide("yes");
    else if (dx < -THRESHOLD) decide("no");
    else setDx(0);
  };

  if (deckQuery.isLoading) return <p className="muted">Loading…</p>;
  if (!top)
    return (
      <div className="card stack" style={{ textAlign: "center", alignItems: "center" }}>
        <h3>You've gone through every post</h3>
        <p className="muted">
          {savedHere
            ? `You saved ${savedHere} ${savedHere === 1 ? "post" : "posts"} to ask for permission.`
            : "New posts show up here after the next check."}
        </p>
        {(deckQuery.data?.found?.saved ?? 0) > 0 && (
          <button type="button" className="btn" onClick={onShowSaved}>
            See saved posts
          </button>
        )}
        {history.length > 0 && (
          <button type="button" className="btn link small" onClick={undo}>
            Undo the last one
          </button>
        )}
      </div>
    );

  const offset = leaving === "yes" ? 700 : leaving === "no" ? -700 : dx;
  const strength = Math.min(1, Math.abs(offset) / THRESHOLD);
  return (
    <div className="swipe">
      <div className="swipe-main">
        <button
          type="button"
          className="swipe-btn no"
          onClick={() => decide("no")}
          aria-label="Ignore (left arrow)"
          title="Ignore (←)"
        >
          ✕
        </button>
        <div className="swipe-stack">
          {next && (
            <div className="swipe-card behind" key={next.id}>
              <Card p={next} behind />
            </div>
          )}
          <div
            key={top.id}
            className={`swipe-card ${dragging ? "" : "settle"}`}
            style={{ transform: `translateX(${offset}px) rotate(${offset / 22}deg)` }}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
          >
            <span className="swipe-stamp yes" style={{ opacity: offset > 0 ? strength : 0 }}>
              Want it
            </span>
            <span className="swipe-stamp no" style={{ opacity: offset < 0 ? strength : 0 }}>
              Ignore
            </span>
            <Card p={top} />
          </div>
        </div>
        <button
          type="button"
          className="swipe-btn yes"
          onClick={() => decide("yes")}
          aria-label="Want it: save to ask later (right arrow)"
          title="Want it (→)"
        >
          ♥
        </button>
      </div>
      <p className="small muted" style={{ textAlign: "center", margin: 0 }}>
        {left} {left === 1 ? "post" : "posts"} to go through · ← Ignore · → Want it ·{" "}
        <button
          type="button"
          className="btn link small"
          onClick={undo}
          disabled={!history.length}
          title="Undo (Z)"
          style={{ padding: 0 }}
        >
          Undo (Z)
        </button>
        <br />
        Posts you want wait under ♥ Saved, ready to ask.
      </p>
    </div>
  );
}
